import React, { useEffect, useMemo, useRef, useState } from 'react'
import '../scss/button.scss';
import { ReactComponent as WarningIcon } from '../images/warning-icon.svg'
import { ReactComponent as UploadIcon } from '../images/upload-icon.svg'
import { ReactComponent as UploadCloudIcon } from '../images/upload-cloud-icon.svg'
import { ReactComponent as CheckIconChk } from '../images/check-icon-green.svg'
import { ReactComponent as StarfinLogo } from '../images/stashfin-logo.svg'
import { useDispatch, useSelector } from 'react-redux';
import CenteredPopup from '../components/popups/CenteredPopup';
import PageHeader from '../components/PageHeader';
import SelectDropdown from '../components/SelectDropdown';
import { closeUploadModal, openUploadModal, setDocNumValid, setFieldError, setFileData, setFileError, setFileSuccess, setIsUpdating, setPageError, setSelectedDocType, setSelectedDocTypeNumber } from '../lib/features/businessSlice';
import BottomPopup from '../components/popups/BottomPopup';
import { useSkipPageMutation, useSubmitBusinessDetailsMutation } from '../lib/services/businessApi';
import { useLocation, useNavigate } from 'react-router-dom';
import { pushCleverTapEvent } from '../lib/middleware/CtEvents';

function BusinessDetails() {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()
    const fileInputRef = useRef(null)
    const footerRef = useRef(null);
    const { pageerror, fielderror, selectedDocType, docTypeNumber, isUploadModalOpen, isDocNumValid, fileerror, fileData, fileSuccess, isUpdating } = useSelector((state) => state.business);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false)
    const [bottomPosition, setBottomPosition] = useState(10);
    const [submitBusinessDetails, { isLoading }] = useSubmitBusinessDetailsMutation()
    const [skipPage, { isLoading: skippingPage }] = useSkipPageMutation()
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])
    const jwt = useMemo(() => queryParams.get('jwt_token'), [queryParams])
    const secret = useMemo(() => queryParams.get('secret'), [queryParams])
    const handleOpen = () => {
        setIsModalOpen(true);
    }
    const handleClose = () => {
        setIsModalOpen(false);
        dispatch(setPageError(''))
    }
    const modalButtons = [
        { label: 'Close', onClick: handleClose }
    ]
    const docuTypeList = ['GST Number', 'Udyam registration number']

    const handleUploadButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    // Handle file selection
    const handleFileChange = (event) => {
        const files = event.target.files;
        handleFiles(files);
    };

    // Handle drag and drop files
    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);

        const files = event.dataTransfer.files;
        handleFiles(files);
    };

    // Handle drag over event
    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(true);
    };

    // Handle drag leave event
    const handleDragLeave = () => {
        setIsDragging(false);
    };

    // Process files
    const handleFiles = (files) => {
        dispatch(setFileError(''))
        dispatch(setIsUpdating(true))
        const supportedFormats = ['.jpg', '.jpeg', '.png', '.pdf', '.svg'];

        if (files.length > 0) {
            const file = files[0];
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

            if (!supportedFormats.includes(fileExtension)) {
                dispatch(setFileError('Unsupported file format.'));
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                const base64Data = reader.result;
                dispatch(setFileData(base64Data));
            };
            reader.onerror = () => {
                dispatch(setFileError('An error occurred while reading the file. Please try again.'));
            };

            reader.readAsDataURL(file);

            dispatch(setIsUpdating(false))
        } else {
            dispatch(setFileError('No files selected.'));
            dispatch(setIsUpdating(false))
        }
    }

    const handleSubmit = async () => {
        if (!selectedDocType) {
            dispatch(setPageError('Please select document type.'));
        } else if (!docTypeNumber) {
            dispatch(setPageError(`Please enter ${selectedDocType}.`));
        } else {
            try {
                let jwt_token
                if (jwt) {
                    jwt_token = jwt
                    localStorage.setItem('jwt', jwt)
                } else {
                    jwt_token = localStorage.getItem('jwt')
                }
                const document_name = selectedDocType.split(' ').join('_').toLowerCase()
                const scrt = secret ? secret : localStorage.getItem('secret')
                const result = await submitBusinessDetails({ jwt_token, scrt, document_name, fileData, docTypeNumber })
                if ((result.data && (result.data.status === 'error' || result.data.status === false)) || (result && result.status === 'error')) {
                    dispatch(setPageError(result.data.message || 'Some error occured. Please try again.'))
                    handleOpen()
                    if (result.data.message === 'Login expired! Please re-login.') {
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                        
                    }
                } else if (result.data && (result.data.http_code === 201 || result.data.error)) {
                    if (result.data.message === 'Login expired! Please re-login.') {
                        dispatch(setPageError(result.data.message))
                        handleOpen()
                        localStorage.removeItem('jwt');
                        localStorage.removeItem('lastVisitedPage');
                        const phone = localStorage.getItem('phone')
                        localStorage.clear()
                        phone ? navigate(`/?phone=${phone}`) : navigate('/');
                        
                    } else {
                        dispatch(setPageError(result.data.message || result.data.error))
                        handleOpen()
                    }
                } else if (result.error) {
                    dispatch(setPageError('Please check your internet or try again later.'))
                    handleOpen()
                } else {
                    if (result && result.data && result.data.redirect_url) {
                        pushCleverTapEvent({
                            event_name: 'web_document_submitted',
                            event_property: {
                                Source: window.localStorage.getItem('utm_source'),
                                segment: localStorage.getItem('customerSegment'),
                                "document type": selectedDocType,
                                occupation: "self"
                            }
                        })
                        window.location.href = result.data.redirect_url
                    }
                }
            } catch (error) {
                dispatch(setPageError('Something went wrong. Please try again later.'));
            }
        }
    }

    const handleSkip = async () => {
        try {
            let jwt_token
            if (jwt) {
                jwt_token = jwt
                localStorage.setItem('jwt', jwt)
            } else {
                jwt_token = localStorage.getItem('jwt')
            }
            const scrt = secret ? secret : localStorage.getItem('secret')
            const result = await skipPage({ jwt_token, scrt, page: 'business-details' })
            if ((result.data && (result.data.status === 'error' || result.data.status === false)) || (result && result.status === 'error')) {
                dispatch(setPageError(result.data.message || 'Some error occured. Please try again.'))
                handleOpen()
                if (result.data.message === 'Login expired! Please re-login.') {
                    localStorage.removeItem('jwt');
                    localStorage.removeItem('lastVisitedPage');
                    const phone = localStorage.getItem('phone')
                    localStorage.clear()
                    phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    
                }
            } else if (result.data && result.data.http_code === 201) {
                if (result.data.message === 'Login expired! Please re-login.') {
                    dispatch(setPageError(result.data.message))
                    handleOpen()
                    localStorage.removeItem('jwt');
                    localStorage.removeItem('lastVisitedPage');
                    const phone = localStorage.getItem('phone')
                    localStorage.clear()
                    phone ? navigate(`/?phone=${phone}`) : navigate('/');
                    
                } else {
                    dispatch(setPageError(result.data.message))
                    handleOpen()
                }
            } else if (result.error) {
                dispatch(setPageError('Please check your internet or try again later.'))
                handleOpen()
            } else {
                if (result && result.data && result.data.redirect_url) {
                    window.location.href = result.data.redirect_url
                }
            }
        } catch (error) {
            dispatch(setPageError('Something went wrong. Please try again later.'));
        }
    }

    const validateDocTypeNumber = (value, docType) => {
        if (!value) return
        const sanitizedValue = value.replace(/ /g, "");
        switch (docType) {
            case 'GST Number':
                return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][Z][0-9A-Z]$/.test(sanitizedValue);
            case 'Udyog Aadhaar':
                return /^[0-9]{12}$/.test(sanitizedValue);
            case 'Udyam registration number':
                return /^[A-Z0-9]{12}$/.test(sanitizedValue);
            default:
                return false;
        }
    }
    const formatDocTypeNumber = (value, docType) => {
        if (!value) return
        const sanitizedValue = value.replace(/[^A-Za-z0-9]/g, "");
        if (docType === 'GST Number') {
            return sanitizedValue
                .match(/.{1,5}/g)
                ?.join(" ")
                .slice(0, 17);
        }
        if (docType === 'Udyog Aadhaar') {
            return sanitizedValue
                .match(/.{1,4}/g)
                ?.join(" ")
                .slice(0, 14);
        }
        if (docType === 'Udyam registration number') {
            return sanitizedValue
                .match(/.{1,4}/g)
                ?.join(" ")
                .slice(0, 14);
        }
        return sanitizedValue;
    }

    const handleDocNumberUpdate = (e) => {
        const value = e.target.value ? e.target.value.replace(/[^A-Za-z0-9]/g, "") : '';
        const formattedValue = formatDocTypeNumber(value, selectedDocType)
        dispatch(setSelectedDocTypeNumber(formattedValue ? formattedValue : ''))
        const isValid = validateDocTypeNumber(formattedValue, selectedDocType)
        if (value && isValid) {
            dispatch(setFieldError(""));
            dispatch(setDocNumValid(true))
        } else {
            dispatch(setDocNumValid(false))
            dispatch(setFieldError(`Invalid ${selectedDocType} entered.`));
        }
    }
    const handleConfirm = async () => {
        if (!fileData) {
            dispatch(setFileError('Please re-upload file.'))
        } else {
            dispatch(setIsUpdating(true))
            setTimeout(() => {
                dispatch(setFileError(''))
                dispatch(setFileSuccess(''))
                dispatch(closeUploadModal())
                dispatch(setIsUpdating(false))
            }, 1000);
        }
    }
    useEffect(() => {
        //const viewport = window.visualViewport;
        const updateFooterPosition = () => {
            let height = window.visualViewport.height;
            if (!/iPhone|iPad|iPod|Android/.test(window.navigator.userAgent)) {
                height = window.visualViewport.height;
            }
            setBottomPosition(height - window.visualViewport.height + 10);
        }
        updateFooterPosition();
        window.visualViewport.addEventListener("resize", updateFooterPosition);
        window.visualViewport.addEventListener("scroll", updateFooterPosition);

        return () => {
            window.visualViewport.removeEventListener("resize", updateFooterPosition);
            window.visualViewport.removeEventListener("scroll", updateFooterPosition);
        };
    }, [])
    return (
        <div className={`external-wrapper`}>
            <CenteredPopup
                isOpen={isModalOpen}
                buttons={modalButtons}
                onClose={handleClose} icon={<WarningIcon />}>
                <p>{pageerror}</p>
            </CenteredPopup>
            <div className='page-content'>
                <PageHeader />
                <div className='page-heading'>
                    <h2 className='page-title'>Please provide your business details  </h2>
                </div>
                <div className='input-wrapper'>
                    <SelectDropdown
                        name="docuType" id="docuType"
                        options={docuTypeList}
                        labelText='Select document Type'
                        value={selectedDocType ? selectedDocType : ''}
                        onChange={(option) => {
                            dispatch(setSelectedDocType(option))
                            dispatch(setSelectedDocTypeNumber(''))
                            dispatch(setFieldError(''))
                            dispatch(setDocNumValid(false))
                        }}
                    />
                </div>
                {selectedDocType ? <div className="input-wrapper validation-input business-doc-input">
                    <input
                        type="text"
                        autoComplete='off'
                        name="docTypeNumber"
                        placeholder={`Enter ${selectedDocType === 'GST Number' ? 15 : selectedDocType === 'Udyog Aadhaar' ? 12 : selectedDocType === 'Udyam registration number' ? 12 : 0} digit ${selectedDocType}`}
                        maxLength={selectedDocType === 'GST Number' ? 17 : selectedDocType === 'Udyog Aadhaar' ? 14 : selectedDocType === 'Udyam registration number' ? 14 : 0}
                        value={docTypeNumber}
                        onChange={(e) => {
                            handleDocNumberUpdate(e)
                        }}
                        className={`form-control`}
                    />
                    <label>{selectedDocType}</label>
                    {isDocNumValid && <span className='completed'><CheckIconChk /></span>}
                    {fielderror && <div className='error'>{fielderror}</div>}
                </div> : null}
                {isDocNumValid && <div className={fileData ? 'upload-button uploaded' : 'upload-button'} onClick={() => dispatch(openUploadModal())}>
                    <div className='text'>{fileData ? 'Document uploaded' : 'Upload selected document proof'}</div>
                    <div className='icon'>{fileData ? <><div className='image'><img src={fileData} alt='Uploaded' /></div><CheckIconChk /></> : <UploadIcon className='branding-icon' />}</div>
                </div>}
                {fileData ? <div className='reupload' onClick={() => dispatch(openUploadModal())}>Re-upload</div> : null}
                <div className={`skip-link link ${skippingPage ? 'disabled' : ''}`} onClick={handleSkip}>Skip</div>
            </div>
            <div className='bottom-footer p-0' ref={footerRef} style={{ bottom: `${bottomPosition}px` }}>
                <p className='powered-by'>Powered by <StarfinLogo /></p>
                <button type="submit" className={`btn btn-primary ${isLoading || !selectedDocType || !docTypeNumber ? 'disabled' : ''}`} onClick={handleSubmit}>Continue</button>
            </div>
            <BottomPopup
                isOpen={isUploadModalOpen}
                onClose={() => dispatch(closeUploadModal())}
                title="Upload files"
                //content={bankDetails}
                className="upload-modal"
                buttons={[{
                    label: 'Upload & Save',
                    onClick: handleConfirm,
                    disabled: !fileData || isUpdating
                }]}>
                <div className={`upload-box ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onDragLeave={handleDragLeave}>
                    <UploadCloudIcon className='cloud-icon' />
                    <p>Max Size: 3 MB | Supported Formats: .jpg, .jpeg, .png, .pdf, .svg</p>
                    <button type="button" className='btn btn-primary-outlined' onClick={handleUploadButtonClick}>Browse files</button>
                    {fileerror && <div className='error'>{fileerror}</div>}
                    {fileSuccess && <div className='success'>{fileSuccess}</div>}
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                </div>
            </BottomPopup>
        </div>
    )
}

export default BusinessDetails
